
import { Vue, Options, prop } from "vue-class-component";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  formatByCountry,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import {
  CargoV2ListRequest,
  CargoV2MinimumWeightSummaryApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  filter = prop<CargoV2ListRequest>({
    default: () => new CargoV2ListRequest({ status: "success" })
  });
}

@Options({
  components: { Skeleton },
  watch: {
    filter: {
      handler(newVal, oldVal) {
        if (!oldVal) {
          this.fetchSummariesIfShown();
          return;
        }

        const { search: newSearch, ...newFilter } = newVal;
        const { search: oldSearch, ...oldFilter } = oldVal;

        const isDifferent =
          JSON.stringify(newFilter) !== JSON.stringify(oldFilter);

        if (isDifferent) {
          this.fetchSummariesIfShown();
        }
      },
      deep: true
    }
  }
})
export default class CargoV2Dispute extends Vue.with(Props) {
  isExpandMinimumWeightSummary = false;

  get isLoadingCargoDispute(): boolean {
    return CargoV2Controller.isLoadingCargoDispute;
  }

  get shouldShowSummary(): boolean {
    return FlagsPermissionCargo.permission_cargo_dispute_summary_enable.isEnabled();
  }

  get cargoDisputeSummaryData() {
    return CargoV2Controller.cargoDisputeSummaryData;
  }

  get dateRangeDisplay(): string {
    const d = this.cargoDisputeSummaryData;
    if (!d.startDate || !d.endDate) return "";
    const start = this.$moment(d.startDate).format("DD MMM");
    const end = this.$moment(d.endDate).format("DD MMM");
    return `${start} - ${end}`;
  }

  get cargoMinimumWeightSummary() {
    return CargoV2Controller.cargoMinimumWeightSummaryData;
  }

  get volumeWeightFlag(): boolean {
    return (
      FlagsPermissionCargo.validation_3lc_cargo_minimum_weight.getValue() !== 0
    );
  }

  mounted(): void {
    if (this.shouldShowSummary && this.volumeWeightFlag) {
      this.isExpandMinimumWeightSummary = true;
    }
    this.fetchSummariesIfShown();
  }

  fetchSummariesIfShown(): void {
    if (!this.shouldShowSummary) return;

    CargoV2Controller.getCargoDisputeSummary(
      new CargoV2ListRequest({
        originCity: this.filter.originCity
          ? this.filter.originCity
          : AccountController.accountData.account_location.city_code,
        destinationCity: this.filter.destinationCity,
        destinationAirport: this.filter.destinationAirport,
        startDate: this.filter.startDate
          ? new Date(this.filter.startDate).toLocaleDateString("fr-CA")
          : new Date(
              new Date(new Date().setDate(new Date().getDate() - 1))
            ).toLocaleDateString("fr-CA"),
        endDate: this.filter.endDate
          ? new Date(this.filter.endDate).toLocaleDateString("fr-CA")
          : new Date().toLocaleDateString("fr-CA"),
        hubIds: this.filter.hubIds,
        cargoTypes: this.filter.cargoTypes
      })
    );

    if (this.volumeWeightFlag) {
      CargoV2Controller.getCargoMinimumWeightSummary(
        new CargoV2MinimumWeightSummaryApiRequest({
          startDate: this.filter.startDate
            ? new Date(this.filter.startDate).toLocaleDateString("fr-CA")
            : new Date(
                new Date(new Date().setDate(new Date().getDate() - 1))
              ).toLocaleDateString("fr-CA"),
          endDate: this.filter.endDate
            ? new Date(this.filter.endDate).toLocaleDateString("fr-CA")
            : new Date().toLocaleDateString("fr-CA"),
          hubIds: this.filter.hubIds
        })
      );
    }
  }

  async onDownload(cargoType: string): Promise<void> {
    console.log(this.filter);
    try {
      await CargoV2Controller.downloadExcelCargoSummaryDispute({
        cargoType,
        params: this.filter
      });
    } catch (error) {
      console.log(error);
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargo.messages.downloadDisputeSummaryFailed"),
          undefined,
          () => MainAppController.closeErrorMessage()
        )
      );
    }
  }

  async onDownloadMinimumVolume(): Promise<void> {
    try {
      await CargoV2Controller.downloadMinimumVolume(this.filter);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargo.messages.downloadMinimumWeightFailed"),
          undefined,
          () => MainAppController.closeErrorMessage()
        )
      );
    }
  }

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  formatCurrency(percentage: number): string {
    return formatByCountry(percentage, { type: "percentage", country: this.countryCode });
  }
}
